import React, { useState, useEffect, useMemo } from 'react'
import { useRouteMatch } from 'react-router'
import classNames from 'classnames'
import { useSelector } from 'react-redux'

import Footer from './footer'
import Navigation from './navigation'
import styles from '../component-styles/sitewrapper.module.scss'
import UplifterNavBar from './uplifter-nav-bar'
import OnboardingProgressWidget from './onboarding-widget'
import useOnboarding from '../hooks/useOnboarding'
import { GraphQLState } from '../store/graphql-reducer'
import { getLocalItem } from '../local-client/local-client'

interface Props {
  skeleton?: boolean
  children?: React.ReactNode
  noFooter?: boolean
  className?: string
  justLogo?: boolean
  uplifterNavBar?: boolean
  grey?: boolean
  greyAndWhite?: boolean
  twoPanelsSignUp?: boolean
}

export default function SiteWrapper({
  skeleton,
  children,
  noFooter,
  className,
  justLogo,
  uplifterNavBar,
  twoPanelsSignUp,
  grey = false,
  greyAndWhite = false,
}: Props): React.ReactElement {
  const c = classNames(styles.site, {
    [styles.justLogoSite]: justLogo || uplifterNavBar,
    [styles.grey]: grey,
    [styles.greyAndWhite]: greyAndWhite,
    [styles.twoPanelsSignUp]: twoPanelsSignUp,
  })

  const [showOnboardingWidget, setShowOnboardingWidget] = useState(false)

  const {
    graphql: { currentAccount },
  }: {
    graphql: GraphQLState
  } = useSelector((s: any): any => s)

  const accountID = currentAccount?.accountID || ''

  useEffect(() => {
    if (accountID) {
      let widgetHidden = getLocalItem('user-session-data')
      widgetHidden = widgetHidden === null ? false : widgetHidden[accountID]
      widgetHidden = widgetHidden ? widgetHidden.hideOnboardingWidget : false
      setShowOnboardingWidget(!widgetHidden)
    }
  }, [accountID])

  const { fullOnboardingSections, setFullOnboardingSections } = useOnboarding()

  const completedSections = useMemo(() => {
    if (fullOnboardingSections) {
      return fullOnboardingSections.filter((section) => {
        return (
          (section.sectionCompleted &&
            (section.sectionSkipped || !section.sectionSkippedBefore)) ||
          section.sectionSkipped
        )
      }).length
    }
    return null
  }, [fullOnboardingSections])

  const widgetMatch = useRouteMatch(['/welcome'])

  return (
    <div className={c}>
      {uplifterNavBar ? (
        <UplifterNavBar />
      ) : (
        <Navigation
          grey={grey || greyAndWhite}
          skeleton={skeleton}
          justLogo={justLogo}
          showWelcome={
            completedSections !== null &&
            completedSections < fullOnboardingSections.length
          }
        />
      )}
      <main className={className}>
        {children}
        {currentAccount &&
          showOnboardingWidget &&
          !widgetMatch &&
          fullOnboardingSections && (
            <OnboardingProgressWidget
              fullOnboardingSections={fullOnboardingSections}
              setFullOnboardingSections={setFullOnboardingSections}
              setShowOnboardingWidget={setShowOnboardingWidget}
              completedSections={completedSections}
              currentAccount={currentAccount.accountID}
            />
          )}
      </main>
      {!noFooter && <Footer />}
    </div>
  )
}
